// Internal-only operational flags (statement/AI pause). Deliberately NOT part of
// the SDK or public API — the admin console reads/sets them via direct fetch.
import type { ReferralStage } from '@commissionsight/shared';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787/v1';
const TOKEN_KEY = 'cs.admin.token';

function headers(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  const h: Record<string, string> = { 'content-type': 'application/json' };
  if (token) h.authorization = `Bearer ${token}`;
  return h;
}

// OTP login is an internal staff flow, not part of the public SDK surface.
const base = () => BASE_URL.replace(/\/$/, '');

export interface AiBuildResult {
  action: 'reuse' | 'reuse_candidate' | 'build';
  signatureHash: string;
  configId: string | null;
  built: boolean;
  coverage?: { mapped: number; total: number };
}

/**
 * Train AI on a sample statement: reuse a known mapping for this statement shape or
 * build a new one with the platform Opus model (statement-signature library).
 * Multipart, so we omit the JSON content-type and let the browser set the boundary.
 */
export async function adminAiBuildMapping(carrierId: string, file: File): Promise<AiBuildResult> {
  const token = localStorage.getItem(TOKEN_KEY);
  const form = new FormData();
  form.set('file', file);
  const r = await fetch(`${base()}/admin/carriers/${carrierId}/mapping/ai-build`, {
    method: 'POST',
    headers: token ? { authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  if (!r.ok) {
    const b = (await r.json().catch(() => null)) as { title?: string } | null;
    throw new Error(b?.title ?? `Request failed (${r.status})`);
  }
  return (await r.json()) as AiBuildResult;
}

export interface AdminConfigDraft {
  id: string;
  type: string;
  accountId: string;
  accountName: string;
  carrierId: string | null;
  carrierName: string | null;
  productLine: string | null;
  payload: unknown;
  createdAt: number;
}

export async function listConfigDrafts(): Promise<{ drafts: AdminConfigDraft[] }> {
  const r = await fetch(`${base()}/config-drafts`, { headers: headers() });
  if (!r.ok) throw new Error(`Request failed (${r.status})`);
  return r.json() as Promise<{ drafts: AdminConfigDraft[] }>;
}

export async function applyConfigDraft(id: string): Promise<{ id: string; applied: boolean }> {
  const r = await fetch(`${base()}/config-drafts/${id}/apply`, {
    method: 'POST',
    headers: headers(),
  });
  if (!r.ok) {
    const b = (await r.json().catch(() => null)) as { title?: string } | null;
    throw new Error(b?.title ?? `Request failed (${r.status})`);
  }
  return r.json() as Promise<{ id: string; applied: boolean }>;
}

export async function rejectConfigDraft(id: string): Promise<{ id: string; rejected: boolean }> {
  const r = await fetch(`${base()}/config-drafts/${id}/reject`, {
    method: 'POST',
    headers: headers(),
  });
  if (!r.ok) {
    const b = (await r.json().catch(() => null)) as { title?: string } | null;
    throw new Error(b?.title ?? `Request failed (${r.status})`);
  }
  return r.json() as Promise<{ id: string; rejected: boolean }>;
}

// --- AI ops: statement-signature library + AI usage ---

export interface AdminSignature {
  id: string;
  signatureHash: string;
  carrierId: string;
  carrierName: string;
  fileType: string;
  status: 'candidate' | 'verified' | 'rejected';
  confidence: number | null;
  builtBy: string;
  model: string | null;
  matchCount: number;
  cleanIngestCount: number;
  distinctAccounts: number;
  configId: string | null;
  createdAt: number;
  verifiedAt: number | null;
}

export async function listSignatures(): Promise<{ signatures: AdminSignature[] }> {
  const r = await fetch(`${base()}/signatures`, { headers: headers() });
  if (!r.ok) throw new Error(`Request failed (${r.status})`);
  return r.json() as Promise<{ signatures: AdminSignature[] }>;
}

async function signatureAction(
  id: string,
  action: 'verify' | 'reject',
): Promise<{ id: string; status: string }> {
  const r = await fetch(`${base()}/signatures/${id}/${action}`, {
    method: 'POST',
    headers: headers(),
  });
  if (!r.ok) {
    const b = (await r.json().catch(() => null)) as { title?: string } | null;
    throw new Error(b?.title ?? `Request failed (${r.status})`);
  }
  return r.json() as Promise<{ id: string; status: string }>;
}

export const verifySignature = (id: string) => signatureAction(id, 'verify');
export const rejectSignature = (id: string) => signatureAction(id, 'reject');

export interface AiUsageSummary {
  period: string;
  totals: { queries: number; tokens: number; costCents: number };
  byAccount: {
    accountId: string;
    accountName: string;
    queries: number;
    tokens: number;
    costCents: number;
  }[];
}

export async function getAiUsageSummary(period?: string): Promise<AiUsageSummary> {
  const q = period ? `?period=${encodeURIComponent(period)}` : '';
  const r = await fetch(`${base()}/ai-usage${q}`, { headers: headers() });
  if (!r.ok) throw new Error(`Request failed (${r.status})`);
  return r.json() as Promise<AiUsageSummary>;
}

export async function requestOtp(email: string): Promise<{ status: string }> {
  const r = await fetch(`${base()}/auth/otp/request`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!r.ok) throw new Error(`Failed to send code (${r.status})`);
  return r.json();
}

export async function verifyOtp(
  email: string,
  code: string,
): Promise<{
  token: string;
  account: { accountId: string; status: string } | null;
  role: string;
}> {
  const r = await fetch(`${base()}/auth/otp/verify`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'title' in body
        ? String((body as { title: unknown }).title)
        : 'Invalid code';
    throw new Error(message);
  }
  return r.json();
}

export interface Impersonation {
  token: string;
  expiresAt: string;
  readOnly: boolean;
  user: { id: string; email: string };
  account: { id: string; name: string | null; slug: string | null };
  appUrl: string;
}

/**
 * Mint a short-lived, READ-ONLY session for a customer user and open the web app
 * as them. The token is handed to the app via the URL fragment (never sent to a
 * server, kept out of logs); the app picks it up, stores it, and shows a support
 * banner. Internal-only — deliberately not on the SDK surface.
 */
export async function impersonateUser(userId: string): Promise<Impersonation> {
  const r = await fetch(`${base()}/admin/users/${userId}/impersonate`, {
    method: 'POST',
    headers: headers(),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => null);
    throw new Error(body?.title ?? `Failed to start impersonation (${r.status})`);
  }
  const data: Impersonation = await r.json();
  window.open(
    `${data.appUrl.replace(/\/$/, '')}/#impersonate=${encodeURIComponent(data.token)}`,
    '_blank',
    'noopener',
  );
  return data;
}

/**
 * Re-send the teammate invite email to an existing account-linked user. The
 * back-office "Invite user" creates the row AND emails; this resends it (e.g. the
 * person never got the first one). Internal-only — not on the SDK surface.
 */
export async function reinviteUser(userId: string): Promise<{ sent: boolean; email: string }> {
  const r = await fetch(`${base()}/admin/users/${userId}/invite`, {
    method: 'POST',
    headers: headers(),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => null);
    throw new Error(body?.title ?? `Failed to re-invite (${r.status})`);
  }
  return r.json();
}

/** One row of a user's access/audit log (sign-ins carry IP + browser/OS). */
export interface UserAccessEntry {
  id: string;
  action: string;
  target: string | null;
  ip: string | null;
  meta: { browser?: string | null; os?: string | null; userAgent?: string | null } | null;
  createdAt: number | string;
}

/** The audit/access log for one user (their sign-ins + actions). Internal-only. */
export async function getUserAccessLog(userId: string): Promise<UserAccessEntry[]> {
  const r = await fetch(`${base()}/admin/users/${userId}/access-log`, { headers: headers() });
  if (!r.ok) {
    const body = await r.json().catch(() => null);
    throw new Error(body?.title ?? `Failed to load access log (${r.status})`);
  }
  return (await r.json()).data as UserAccessEntry[];
}

/** One account in the "needs attention" health board. */
export interface HealthRow {
  accountId: string;
  name: string;
  status: string;
  estimateCarriers: number;
  coveragePct: number | null;
  owed: number;
  warnings: string[];
  lastJobAt: number | null;
  weeksQuiet: number | null;
  issues: string[];
}
export interface HealthBoard {
  accounts: HealthRow[];
  summary: {
    totalAccounts: number;
    needAttention: number;
    byIssue: Record<string, number>;
    quietBuckets: number[];
  };
}

/** The owed-quality + churn-risk feed (accounts needing attention). Internal-only. */
export async function getHealthBoard(): Promise<HealthBoard> {
  const r = await fetch(`${base()}/admin/health-board`, { headers: headers() });
  if (!r.ok) {
    const body = await r.json().catch(() => null);
    throw new Error(body?.title ?? `Failed to load health board (${r.status})`);
  }
  return r.json();
}

/** One account-action audit event. */
export interface AccountAuditEntry {
  id: string;
  actor: string;
  action: string;
  target: string | null;
  ip: string | null;
  meta: Record<string, unknown> | null;
  createdAt: number | string;
}

/** Account-action audit (who provisioned, paused, changed rates, impersonated…). */
export async function getAccountAudit(accountId: string): Promise<AccountAuditEntry[]> {
  const r = await fetch(`${base()}/admin/accounts/${accountId}/audit`, { headers: headers() });
  if (!r.ok) {
    const body = await r.json().catch(() => null);
    throw new Error(body?.title ?? `Failed to load account audit (${r.status})`);
  }
  return (await r.json()).data as AccountAuditEntry[];
}

/** Disable (suspend) or re-enable an account. Access-only — no data is touched. */
export async function setAccountAccess(
  accountId: string,
  action: 'suspend' | 'reactivate',
): Promise<{ id: string; status: string }> {
  const r = await fetch(`${BASE_URL.replace(/\/$/, '')}/admin/accounts/${accountId}/${action}`, {
    method: 'POST',
    headers: headers(),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => null)) as { title?: string } | null;
    throw new Error(body?.title ?? `Failed to ${action} the account (${r.status})`);
  }
  return r.json();
}

export interface ProcessingFlags {
  processingPaused: boolean;
  aiPaused: boolean;
}

export async function getProcessing(accountId: string): Promise<ProcessingFlags> {
  const r = await fetch(`${BASE_URL.replace(/\/$/, '')}/admin/accounts/${accountId}/processing`, {
    headers: headers(),
  });
  if (!r.ok) throw new Error(`Failed to load processing flags (${r.status})`);
  return r.json();
}

export async function setProcessing(
  accountId: string,
  body: Partial<ProcessingFlags>,
): Promise<ProcessingFlags> {
  const r = await fetch(`${BASE_URL.replace(/\/$/, '')}/admin/accounts/${accountId}/processing`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`Failed to update processing flags (${r.status})`);
  return r.json();
}

export interface AiUsageBucket {
  period: string;
  queries: number;
  tokens: number;
  costCents: number;
}
export interface AiUsage {
  accountId: string;
  total: { queries: number; tokens: number; costCents: number };
  current: AiUsageBucket;
  byMonth: AiUsageBucket[];
}

export async function getAiUsage(accountId: string): Promise<AiUsage> {
  const r = await fetch(`${BASE_URL.replace(/\/$/, '')}/admin/accounts/${accountId}/ai-usage`, {
    headers: headers(),
  });
  if (!r.ok) throw new Error(`Failed to load AI usage (${r.status})`);
  return r.json();
}

export interface Workspace {
  id: string;
  name: string;
  isDefault: boolean;
  rateCents: number | null;
}
export interface WorkspacesView {
  accountId: string;
  workspacesEnabled: boolean;
  workspaces: Workspace[];
}

function adminWs(accountId: string, path = ''): string {
  return `${BASE_URL.replace(/\/$/, '')}/admin/accounts/${accountId}/workspaces${path}`;
}

export async function getWorkspaces(accountId: string): Promise<WorkspacesView> {
  const r = await fetch(adminWs(accountId), { headers: headers() });
  if (!r.ok) throw new Error(`Failed to load workspaces (${r.status})`);
  return r.json();
}

export interface FeatureDef {
  label: string;
  default: boolean;
  column?: string;
  roadmap?: boolean;
}
export interface EntitlementsView {
  accountId: string;
  entitlements: Record<string, boolean>;
  features: Record<string, FeatureDef>;
}

// --- Email statement ingest: per-carrier inbound addresses (admin-managed). ---
export interface InboundAlias {
  id: string;
  carrierId: string;
  carrierName: string | null;
  alias: string;
  address: string;
  allowedSenderDomain: string | null;
  filenamePeriodRegex: string | null;
  enabled: boolean;
  lastReceivedAt: number | null;
  lastStatus: string | null;
}

export async function getInboundAliases(
  accountId: string,
): Promise<{ domain: string; aliases: InboundAlias[] }> {
  const r = await fetch(`${base()}/admin/accounts/${accountId}/inbound-aliases`, {
    headers: headers(),
  });
  if (!r.ok) throw new Error(`Failed to load inbound addresses (${r.status})`);
  return r.json();
}

export async function createInboundAlias(
  accountId: string,
  input: { carrierId: string; allowedSenderDomain?: string; filenamePeriodRegex?: string },
): Promise<{ id: string; address: string }> {
  const r = await fetch(`${base()}/admin/accounts/${accountId}/inbound-aliases`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error(`Failed to create address (${r.status})`);
  return r.json();
}

export async function deleteInboundAlias(accountId: string, id: string): Promise<void> {
  const r = await fetch(`${base()}/admin/accounts/${accountId}/inbound-aliases/${id}`, {
    method: 'DELETE',
    headers: headers(),
  });
  if (!r.ok) throw new Error(`Failed to remove address (${r.status})`);
}

export async function getCarriers(): Promise<{ id: string; name: string }[]> {
  const r = await fetch(`${base()}/admin/carriers`, { headers: headers() });
  if (!r.ok) throw new Error(`Failed to load carriers (${r.status})`);
  const body = await r.json();
  return (body.data ?? body.carriers ?? []).map((x: { id: string; name: string }) => ({
    id: x.id,
    name: x.name,
  }));
}

// --- Carrier brands (groups): a parent over per-product carrier records, used
// for upload-time content routing. Admin-only management. ----------------------
export interface AdminCarrierGroup {
  id: string;
  name: string;
  slug: string;
  members: { id: string; name: string }[];
}

export async function listCarrierGroups(): Promise<AdminCarrierGroup[]> {
  const r = await fetch(`${base()}/admin/carrier-groups`, { headers: headers() });
  if (!r.ok) throw new Error(`Failed to load carrier groups (${r.status})`);
  return (await r.json()).data as AdminCarrierGroup[];
}

export async function createCarrierGroup(
  name: string,
  slug: string,
): Promise<{ id: string; name: string; slug: string }> {
  const r = await fetch(`${base()}/admin/carrier-groups`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ name, slug }),
  });
  if (!r.ok) {
    const b = await r.json().catch(() => null);
    throw new Error(b?.title ?? `Failed to create brand (${r.status})`);
  }
  return r.json();
}

/** Assign a carrier to a brand, or clear it (groupId: null). */
export async function setCarrierGroup(
  carrierId: string,
  groupId: string | null,
): Promise<{ id: string; groupId: string | null }> {
  const r = await fetch(`${base()}/admin/carriers/${carrierId}/group`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify({ groupId }),
  });
  if (!r.ok) {
    const b = await r.json().catch(() => null);
    throw new Error(b?.title ?? `Failed to assign brand (${r.status})`);
  }
  return r.json();
}

/** One customer "add my carrier" request in the operator approval queue. */
export interface CarrierRequestView {
  id: string;
  accountId: string;
  accountName: string;
  requestedName: string;
  note: string | null;
  status: 'pending' | 'approved' | 'merged' | 'rejected';
  fileType: 'csv' | 'xlsx';
  fileName: string | null;
  headers: string[];
  inferredProductLine: string | null;
  resolvedCarrierId: string | null;
  resolvedCarrierName: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

export async function listCarrierRequests(status?: string): Promise<CarrierRequestView[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const r = await fetch(`${base()}/admin/carrier-requests${qs}`, { headers: headers() });
  if (!r.ok) throw new Error(`Failed to load carrier requests (${r.status})`);
  const body = (await r.json()) as { data: CarrierRequestView[] };
  return body.data;
}

export interface CarrierRequestDecision {
  id: string;
  status: string;
  carrier?: { id: string; name: string; slug: string };
  configBuilt?: boolean;
  buildReason?: string | null;
}

export async function approveCarrierRequest(
  id: string,
  body: { name?: string; productLine?: string; groupId?: string | null },
): Promise<CarrierRequestDecision> {
  const r = await fetch(`${base()}/admin/carrier-requests/${id}/approve`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const b = await r.json().catch(() => null);
    throw new Error(b?.title ?? `Approve failed (${r.status})`);
  }
  return r.json();
}

export async function mergeCarrierRequest(
  id: string,
  carrierId: string,
): Promise<CarrierRequestDecision> {
  const r = await fetch(`${base()}/admin/carrier-requests/${id}/merge`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ carrierId }),
  });
  if (!r.ok) {
    const b = await r.json().catch(() => null);
    throw new Error(b?.title ?? `Merge failed (${r.status})`);
  }
  return r.json();
}

export async function rejectCarrierRequest(
  id: string,
  note?: string,
): Promise<CarrierRequestDecision> {
  const r = await fetch(`${base()}/admin/carrier-requests/${id}/reject`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(note ? { note } : {}),
  });
  if (!r.ok) {
    const b = await r.json().catch(() => null);
    throw new Error(b?.title ?? `Reject failed (${r.status})`);
  }
  return r.json();
}

export interface AccountInsight {
  id: string;
  accountId: string;
  note: string;
  source: 'admin' | 'system';
  createdBy: string | null;
  createdAt: string;
  archivedAt: string | null;
}

export async function listInsights(accountId: string): Promise<{ data: AccountInsight[] }> {
  const r = await fetch(`${base()}/admin/accounts/${accountId}/insights`, { headers: headers() });
  if (!r.ok) throw new Error(`Failed to load insights (${r.status})`);
  return r.json();
}

export async function addInsight(accountId: string, note: string): Promise<AccountInsight> {
  const r = await fetch(`${base()}/admin/accounts/${accountId}/insights`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ note }),
  });
  if (!r.ok) {
    const b = await r.json().catch(() => null);
    throw new Error(b?.title ?? `Add insight failed (${r.status})`);
  }
  return r.json();
}

export async function archiveInsight(accountId: string, insightId: string): Promise<void> {
  const r = await fetch(`${base()}/admin/accounts/${accountId}/insights/${insightId}`, {
    method: 'DELETE',
    headers: headers(),
  });
  if (!r.ok) throw new Error(`Archive insight failed (${r.status})`);
}

export async function getEntitlements(accountId: string): Promise<EntitlementsView> {
  const r = await fetch(`${base()}/admin/accounts/${accountId}/entitlements`, {
    headers: headers(),
  });
  if (!r.ok) throw new Error(`Failed to load entitlements (${r.status})`);
  return r.json();
}

export async function setEntitlement(
  accountId: string,
  feature: string,
  enabled: boolean,
): Promise<EntitlementsView> {
  const r = await fetch(`${base()}/admin/accounts/${accountId}/entitlements`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify({ feature, enabled }),
  });
  if (!r.ok) throw new Error(`Failed to update entitlement (${r.status})`);
  return r.json();
}

export async function setWorkspacesEnabled(accountId: string, enabled: boolean): Promise<void> {
  const r = await fetch(adminWs(accountId, '-enabled'), {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify({ enabled }),
  });
  if (!r.ok) throw new Error(`Failed to update feature flag (${r.status})`);
}

export async function createWorkspace(
  accountId: string,
  body: { name: string; rateCents?: number | null },
): Promise<Workspace> {
  const r = await fetch(adminWs(accountId), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`Failed to create workspace (${r.status})`);
  return r.json();
}

export async function updateWorkspace(
  accountId: string,
  wsId: string,
  body: { name?: string; rateCents?: number | null },
): Promise<Workspace> {
  const r = await fetch(adminWs(accountId, `/${wsId}`), {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`Failed to update workspace (${r.status})`);
  return r.json();
}

export type ProductLine = 'major_medical' | 'medicare' | 'ancillary';
export interface RateScheduleRow {
  id: string;
  workspaceId: string | null;
  carrierId: string | null;
  productLine: ProductLine;
  config: Record<string, unknown>;
  isActive: boolean;
}

function rsUrl(accountId: string, path = ''): string {
  return `${BASE_URL.replace(/\/$/, '')}/admin/accounts/${accountId}/rate-schedules${path}`;
}

export async function getRateSchedules(accountId: string): Promise<RateScheduleRow[]> {
  const r = await fetch(rsUrl(accountId), { headers: headers() });
  if (!r.ok) throw new Error(`Failed to load rate schedules (${r.status})`);
  return (await r.json()).schedules;
}

export async function createRateSchedule(
  accountId: string,
  body: { workspaceId?: string | null; carrierId?: string | null; config: unknown },
): Promise<{ id: string; productLine: ProductLine }> {
  const r = await fetch(rsUrl(accountId), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const msg = await r.json().catch(() => null);
    throw new Error(msg?.title ?? `Failed to create schedule (${r.status})`);
  }
  return r.json();
}

export async function updateRateSchedule(
  accountId: string,
  scheduleId: string,
  body: { config?: unknown; isActive?: boolean },
): Promise<void> {
  const r = await fetch(rsUrl(accountId, `/${scheduleId}`), {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const msg = await r.json().catch(() => null);
    throw new Error(msg?.title ?? `Failed to update schedule (${r.status})`);
  }
}

// --- Cold-outreach marketing (separate marketing D1; admin-only) -------------

export interface MktCampaign {
  id: string;
  name: string;
  segment: string | null;
  maxTier: number | null;
  /** Lead-status bucket to enroll from (mirrors the Leads filter); null = enrollable. */
  leadStatus: LeadStatus | null;
  dailyCap: number;
  fromName: string;
  fromEmail: string;
  replyTo: string | null;
  bcc: string | null;
  brief: string | null;
  /** Raw auto-draft toggle the cron reads: draft (off) | active (cron drafts) | paused. */
  status: 'draft' | 'active' | 'paused';
  /**
   * Derived lifecycle for display badges/filters: 'active' once the campaign has
   * real outreach (or auto-drafting is on), 'paused' if paused, else 'draft'. Use
   * this for the badge; use `status` only for the auto-draft toggle control.
   */
  lifecycle?: 'draft' | 'active' | 'paused';
  counts?: Record<string, number>;
  sentToday?: number;
  remainingToday?: number;
}
export interface MktThread {
  campaignId: string;
  leadId: string;
  status: string;
  company: string | null;
  name: string | null;
  email: string | null;
  tier: number;
  messageCount: number;
  lastMessage: {
    direction: 'outbound' | 'inbound';
    status: string;
    subject: string | null;
    snippet: string;
    at: number;
  } | null;
}
export interface MktMessage {
  id: string;
  direction: 'outbound' | 'inbound';
  status: 'draft' | 'revised' | 'sent' | 'failed' | 'received' | 'bounced';
  subject: string | null;
  body: string;
  aiGenerated: boolean;
  createdAt: number;
  sentAt: number | null;
}

export interface MktLead {
  id: string;
  tier: number;
  icpScore: number | null;
  segment: string | null;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  employees: number | null;
  domain: string | null;
  websiteCheckedUrl: string | null;
  /** Present in list/detail: true if the address is on the do-not-contact list. */
  suppressed?: boolean;
  /** Why it's suppressed: hard_bounce, spam_complaint, reply_opt_out, etc. */
  suppressedReason?: string | null;
}

export interface MktEnrollment {
  id: string;
  campaignId: string;
  campaignName: string;
  status: string;
  createdAt: number;
}
export interface MktTimelineMessage {
  id: string;
  campaignId: string;
  campaignName: string | null;
  direction: 'outbound' | 'inbound';
  status: 'draft' | 'revised' | 'sent' | 'failed' | 'received' | 'bounced';
  subject: string | null;
  body: string;
  aiGenerated: boolean;
  createdAt: number;
  sentAt: number | null;
}
export interface MktLeadDetail {
  lead: MktLead;
  enrollments: MktEnrollment[];
  timeline: MktTimelineMessage[];
  suppressed: boolean;
  suppressedReason?: string | null;
  sentCount: number;
  replyCount: number;
}

const mkt = (path: string) => `${base()}/admin/marketing${path}`;
async function mktJson<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(mkt(path), { headers: headers(), ...init });
  if (!r.ok) {
    const b = await r.json().catch(() => null);
    throw new Error(b?.title ?? `Request failed (${r.status})`);
  }
  return r.json() as Promise<T>;
}

/** Lead status buckets shown as filter chips in the Leads browser. */
export type LeadStatus =
  | 'all'
  | 'enrollable'
  | 'sent'
  | 'bounced'
  | 'invalid'
  | 'risky'
  | 'unsubscribed'
  | 'ineligible';

export const listLeads = (
  opts: {
    segment?: string;
    maxTier?: number;
    q?: string;
    limit?: number;
    status?: LeadStatus;
    suppressed?: 'hide' | 'only' | 'all';
  } = {},
) => {
  const qp = new URLSearchParams();
  if (opts.segment) qp.set('segment', opts.segment);
  if (opts.maxTier != null) qp.set('maxTier', String(opts.maxTier));
  if (opts.q) qp.set('q', opts.q);
  if (opts.limit) qp.set('limit', String(opts.limit));
  if (opts.status && opts.status !== 'all') qp.set('status', opts.status);
  if (opts.suppressed && opts.suppressed !== 'all') qp.set('suppressed', opts.suppressed);
  const qs = qp.toString();
  return mktJson<{ data: MktLead[] }>(`/leads${qs ? `?${qs}` : ''}`).then((b) => b.data);
};

export interface LeadFacets {
  total: number;
  counts: {
    enrollable: number;
    sent: number;
    bounced: number;
    invalid: number;
    risky: number;
    unsubscribed: number;
    ineligible: number;
    suppressed: number;
  };
  /** Enrollable lead counts per segment ('(none)' = unlabeled). */
  segments: { segment: string; enrollable: number }[];
}
export const getLeadFacets = () => mktJson<LeadFacets>('/leads/facets');

export const getLead = (id: string) => mktJson<MktLeadDetail>(`/leads/${id}`);
export const listCampaigns = () =>
  mktJson<{ data: MktCampaign[] }>('/campaigns').then((b) => b.data);
export const getCampaign = (id: string) => mktJson<MktCampaign>(`/campaigns/${id}`);
export const createCampaign = (body: Partial<MktCampaign>) =>
  mktJson<{ id: string }>('/campaigns', { method: 'POST', body: JSON.stringify(body) });
export const updateCampaign = (id: string, body: Partial<MktCampaign> & { status?: string }) =>
  mktJson<{ id: string }>(`/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const deleteCampaign = (id: string) =>
  mktJson<{ id: string; deleted: boolean }>(`/campaigns/${id}`, { method: 'DELETE' });

export interface MktSettings {
  /** Editable ceiling for each campaign's daily cold-email send cap. */
  maxDailyCap: number;
}
export const getMarketingSettings = () => mktJson<MktSettings>('/settings');
export const updateMarketingSettings = (maxDailyCap: number) =>
  mktJson<MktSettings>('/settings', { method: 'PUT', body: JSON.stringify({ maxDailyCap }) });
export const enrollLeads = (id: string, limit: number) =>
  mktJson<{ enrolled: number }>(`/campaigns/${id}/enroll`, {
    method: 'POST',
    body: JSON.stringify({ limit }),
  });
export const draftNow = (id: string) =>
  mktJson<{ drafted: number; queued?: number; failed?: number; reason?: string }>(
    `/campaigns/${id}/draft-now`,
    { method: 'POST' },
  );
export const listThreads = (id: string, status?: string) =>
  mktJson<{ data: MktThread[] }>(
    `/campaigns/${id}/threads${status ? `?status=${status}` : ''}`,
  ).then((b) => b.data);
export const getThreadMessages = (id: string, leadId: string) =>
  mktJson<{ data: MktMessage[] }>(`/campaigns/${id}/leads/${leadId}/messages`).then((b) => b.data);
export const createDraft = (id: string, leadId: string, body: { subject: string; body: string }) =>
  mktJson<{ id: string }>(`/campaigns/${id}/leads/${leadId}/draft`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
export const updateMessage = (messageId: string, patch: { subject?: string; body?: string }) =>
  mktJson<{ id: string; subject: string | null; body: string; status: string }>(
    `/messages/${messageId}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
export interface MktSummary {
  campaigns: { total: number; active: number; paused: number; draft: number };
  leads: { total: number; enrolled: number };
  funnel: {
    queued: number;
    drafted: number;
    sent: number;
    replied: number;
    bounced: number;
    unsubscribed: number;
    skipped: number;
    total: number;
  };
  messages: {
    drafts: number;
    sent: number;
    delivered: number;
    delayed: number;
    bounced: number;
    replies: number;
    /** Sent + delivered + recipient not bounce/unsub-suppressed — clean inbox landings. */
    landed: number;
  };
  suppressions: { total: number; bounced: number; complaints: number; optouts: number };
  /** Activity since the start of the current UTC day (sent + suppressions). */
  today: { sent: number; unsubs: number; bounced: number; landed: number };
  rates: { replyRatePct: number; bounceRatePct: number; deliveredRatePct: number };
}
export const getMarketingSummary = () => mktJson<MktSummary>('/summary');

/** A flat send-history row for the campaign Audit tab: an outbound email that
 * went out, with its latest derived status. */
export interface MktAuditRow {
  id: string;
  email: string | null;
  company: string | null;
  name: string | null;
  subject: string | null;
  sentAt: number;
  status: 'sent' | 'delivered' | 'replied' | 'bounced' | 'complaint' | 'unsubscribed';
}
export const getCampaignAudit = (id: string) =>
  mktJson<{ data: MktAuditRow[] }>(`/campaigns/${id}/audit`).then((b) => b.data);

/** Counts of /register submissions the invisible bot traps blocked. Watch
 * `tooFast` — the only trap that could ever catch a real (very fast) human. */
export interface RegisterBlocks {
  total: number;
  honeypot: number;
  tooFast: number;
  last24h: number;
  last7d: number;
}
export async function getRegisterBlocks(): Promise<RegisterBlocks> {
  const r = await fetch(`${base()}/admin/security/register-blocks`, { headers: headers() });
  if (!r.ok) throw new Error(`Failed to load register blocks (${r.status})`);
  return r.json();
}

// One-off seed/test send through the same Resend pipe as a campaign email (same
// identity + unsubscribe headers/footer). Not tied to a campaign, not capped — for
// inbox-placement testing (mail-tester.com, your own Gmail/Outlook, etc.).
export const testSend = (body: { to: string; subject: string; body: string }) =>
  mktJson<{ id: string | null; to: string }>('/test-send', {
    method: 'POST',
    body: JSON.stringify(body),
  });

// AI-draft a realistic sample (subject + body) for the test-send box, using a
// campaign's brief/voice (or the newest campaign's) — the same path real drafts use.
export const testSampleDraft = (campaignId?: string) =>
  mktJson<{ subject: string; body: string; campaignName: string }>('/test-sample', {
    method: 'POST',
    body: JSON.stringify(campaignId ? { campaignId } : {}),
  });

export const regenerateMessage = (messageId: string) =>
  mktJson<{ id: string; subject: string | null; body: string; status: string }>(
    `/messages/${messageId}/regenerate`,
    { method: 'POST' },
  );
export const sendMessage = (messageId: string) =>
  mktJson<{ id: string; status: string; remainingToday: number }>(`/messages/${messageId}/send`, {
    method: 'POST',
  });

// Mark a lead ineligible (not a fit / not ICP): adds them to the do-not-contact
// list, deletes their unsent drafts, and skips their pending enrollments so they're
// never queued or emailed again.
export const disqualifyLead = (leadId: string, reason?: string) =>
  mktJson<{ leadId: string; suppressed: boolean; reason: string; draftsRemoved: number }>(
    `/leads/${leadId}/disqualify`,
    { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
  );

export interface NewLeadInput {
  email: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  title?: string;
  phone?: string;
  segment?: string;
  tier?: number;
  invite?: boolean;
}

/** Manually create a lead (warm inbound). `invite` also emails the register invite. */
export const createLead = (input: NewLeadInput) =>
  mktJson<{
    id: string;
    email: string;
    created: boolean;
    existing: boolean;
    invited?: boolean;
    inviteError?: string;
  }>('/leads', { method: 'POST', body: JSON.stringify(input) });

/** Email an existing lead a personal invite to register. */
export const inviteLead = (leadId: string) =>
  mktJson<{ leadId: string; invited: boolean; id: string | null }>(`/leads/${leadId}/invite`, {
    method: 'POST',
  });

// --- Admin Logs + alert acknowledgement (internal-only; deliberately NOT on the
// public SDK, which is customer-facing). The admin console reads these directly. ---
async function adminJson<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${base()}${path}`, { headers: headers(), ...init });
  if (!r.ok) {
    const b = await r.json().catch(() => null);
    throw new Error(b?.title ?? `Request failed (${r.status})`);
  }
  return r.json() as Promise<T>;
}

export interface AdminAlert {
  id: string;
  severity: 'warning' | 'critical';
  title: string;
  detail: string;
  ts: number;
}
export interface AdminLogEvent {
  id: string;
  ts: number;
  level: 'info' | 'warn' | 'error';
  source: string;
  message: string;
  detail: string | null;
}
export interface AdminLogs {
  generatedAt: number;
  events: AdminLogEvent[];
  alerts: AdminAlert[];
  dismissedCount?: number;
  pagination?: { limit: number; offset: number; hasMore: boolean };
}

export const getAdminLogs = (params: { limit?: number; offset?: number } = {}) => {
  const qs = new URLSearchParams();
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return adminJson<AdminLogs>(`/admin/logs${q ? `?${q}` : ''}`);
};
export const dismissAlerts = (ids: string[]) =>
  adminJson<{ dismissed: number }>('/admin/alerts/dismiss', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
export const clearDismissedAlerts = () =>
  adminJson<{ restored: boolean }>('/admin/alerts/dismissed', { method: 'DELETE' });

// --- Referral program (admin views) -----------------------------------------
export interface ReferralStatsView {
  total: number;
  withStatements: number;
  withPayment: number;
  qualified: number;
  credited: number;
  flagged: number;
  creditedCents: number;
  thisYearCreditedCents: number;
}
export const getReferralStats = () => adminJson<ReferralStatsView>('/admin/referral-stats');

export interface AdminReferralRow {
  referrer: string;
  referred: string;
  referredStatus: string;
  createdAt: number | string;
  statementsAt: number | string | null;
  paymentAt: number | string | null;
  qualifiedAt: number | string | null;
  creditedAt: number | string | null;
  creditCents: number | null;
  flaggedReason: string | null;
  stage: ReferralStage;
}
export const listReferrals = (limit = 100, offset = 0) =>
  adminJson<{ data: AdminReferralRow[]; total: number }>(
    `/admin/referrals?limit=${limit}&offset=${offset}`,
  );

/** Permanently delete an account + its users and all control-plane data. */
export const deleteAccount = (id: string) =>
  adminJson<{
    deleted: boolean;
    accountId: string;
    name: string;
    filesPurged: number;
    usersRemoved: number;
  }>(`/admin/accounts/${id}`, { method: 'DELETE' });

// --- System-event error radar (internal-only) --------------------------------
export type EventLevel = 'critical' | 'error' | 'warn';
export interface SystemEventGroup {
  fingerprint: string;
  level: EventLevel;
  source: string;
  event: string;
  message: string;
  accountId: string | null;
  count: number;
  firstSeen: number;
  lastSeen: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
}
export interface SystemEvents {
  generatedAt: number;
  summary: {
    total: number;
    groups: number;
    last24h: number;
    bySeverity: Record<string, number>;
    bySource: Record<string, number>;
    capped: boolean;
  };
  groups: SystemEventGroup[];
  pagination: { limit: number; offset: number; hasMore: boolean };
}

export const getSystemEvents = (
  params: {
    status?: 'unresolved' | 'resolved' | 'all';
    level?: EventLevel;
    source?: string;
    limit?: number;
    offset?: number;
  } = {},
) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.level) qs.set('level', params.level);
  if (params.source) qs.set('source', params.source);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return adminJson<SystemEvents>(`/admin/system-events${q ? `?${q}` : ''}`);
};

export const resolveSystemEvent = (fingerprint: string) =>
  adminJson<{ resolved: number }>('/admin/system-events/resolve', {
    method: 'POST',
    body: JSON.stringify({ fingerprint }),
  });

export const reopenSystemEvent = (fingerprint: string) =>
  adminJson<{ reopened: number }>('/admin/system-events/reopen', {
    method: 'POST',
    body: JSON.stringify({ fingerprint }),
  });
